'use client';

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { pageCustomerDirectories, pageDirectories } from '@/constant/route';
import { colorPairFromText } from '@/lib/color';
import { performSignOut } from '@/lib/perform-sign-out';
import { isDevEnv } from '@/lib/utils';
import { useRouter } from '@/navigation';
import { useAuthenticationStore } from '@/provider/customs/useAuthenticationStore';
import { getUrlGoToStaffRoute } from '@/server/api/v1/auth/auth.action';
import { AuthRole } from '@/types/enum';
import { DoorOpen, Loader2, LogOut, User, UserRound } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { memo, useEffect, useMemo, useRef, useState } from 'react';

const WaitForFullName = ({
    username,
    setShowName,
}: {
    username?: string;
    setShowName: (state: string) => void;
}) => {
    const saveTimeOut = useRef<NodeJS.Timeout | undefined>();

    useEffect(() => {
        saveTimeOut.current = setTimeout(() => {
            setShowName(username ?? '');
        }, 2000);

        return () => {
            if (saveTimeOut.current) clearTimeout(saveTimeOut.current);
            saveTimeOut.current = undefined;
        };
    }, [setShowName, username]);

    return (
        <div className='flex h-full items-center gap-1 p-1'>
            <Skeleton className='h-6 w-7 rounded-full' />
            <Skeleton className='h-4 w-28 max-md:hidden' />
        </div>
    );
};
const HeaderDropdownMenu = () => {
    const transDef = useTranslations('System');
    const authData = useAuthenticationStore((x) => x.auth);

    const router = useRouter();
    const [isSigningOut, setIsSigningOut] = useState(false);
    const onSignOut = async () => {
        if (isSigningOut) return; // chống bấm kép trong lúc đang đăng xuất
        setIsSigningOut(true);
        // Dùng window.location.replace thay router.push: buộc full page reload để middleware
        // kiểm tra lại session đã xóa và React state (bao gồm auth store) được reset hoàn toàn.
        // router.push chỉ là client-side navigation — người dùng vẫn thấy trang cũ cho đến F5.
        try {
            const result = await performSignOut({ redirect: false });
            window.location.replace(result.url || pageDirectories['Signin']);
        } catch {
            // performSignOut đã bọc lỗi nội bộ; phòng thủ thêm để chắc chắn luôn về trang đăng nhập.
            window.location.replace(pageDirectories['Signin']);
        }
    };

    const onViewProfile = () => {
        const url = pageCustomerDirectories.AccountInfo;
        router.push(url);
    };

    const onGoToStaff = async () => {
        const url = await getUrlGoToStaffRoute();
        if (url?.data) window.location.href = url.data;
    };

    const roles = authData?.Roles?.split(',') || [];
    const isShowBtnSwithToStaff =
        roles.length > 1 && roles.includes(AuthRole.user);

    const [showName, setShowName] = useState<string | undefined>();

    useEffect(() => {
        setShowName(
            authData?.ExtraInfo?.CustomerInfo?.FullName ||
                authData?.ExtraInfo?.UserInfo?.FullName
        );
    }, [authData?.ExtraInfo]);

    const _fullNameShow = showName;

    const icon = useMemo(() => {
        if (_fullNameShow && _fullNameShow.length) {
            const { bg, fg } = colorPairFromText(_fullNameShow);

            return (
                <div
                    className='flex h-6 w-7 items-center justify-center overflow-hidden rounded-full text-xs uppercase'
                    style={{
                        color: fg,
                        background: bg,
                    }}
                >
                    {_fullNameShow
                        .split(' ')
                        .filter((x) => x.length > 0)
                        .slice(0, 2)
                        .map((x) => x[0])
                        .join('')}
                </div>
            );
        }

        return <UserRound size={18} strokeWidth={2} />;
    }, [_fullNameShow]);

    if (!_fullNameShow)
        return (
            <WaitForFullName
                username={authData?.UserName}
                setShowName={setShowName}
            />
        );

    return (
        <>
            <DropdownMenu>
                <DropdownMenuTrigger className='flex h-full items-center gap-1 rounded-md p-1 transition-all'>
                    {icon}
                    <span className='line-clamp-1 min-w-28 max-w-40 text-sm font-medium max-md:hidden'>
                        {_fullNameShow || authData?.UserName}
                    </span>
                </DropdownMenuTrigger>
                <DropdownMenuContent align='end' className='w-48'>
                    <div className='space-y-1 px-2 font-semibold'>
                        <div className='font-semibold'>{_fullNameShow}</div>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                        onClick={onViewProfile}
                        className='cursor-pointer'
                    >
                        <User size={14} className='mr-2' />
                        {transDef('System_Profile')}
                    </DropdownMenuItem>
                    {!!authData ? (
                        <>
                            {isShowBtnSwithToStaff && (
                                <>
                                    <DropdownMenuItem
                                        onClick={onGoToStaff}
                                        className='cursor-pointer'
                                    >
                                        <DoorOpen size={16} className='mr-2' />
                                        Một cửa
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                </>
                            )}
                            <DropdownMenuItem
                                onClick={onSignOut}
                                onSelect={(e) => e.preventDefault()}
                                disabled={isSigningOut}
                                className='cursor-pointer text-red-500'
                            >
                                {isSigningOut ? (
                                    <Loader2
                                        size={14}
                                        className='mr-2 animate-spin'
                                    />
                                ) : (
                                    <LogOut size={14} className='mr-2' />
                                )}
                                {transDef('System_Logout')}
                            </DropdownMenuItem>
                        </>
                    ) : null}
                </DropdownMenuContent>
            </DropdownMenu>
        </>
    );
};

export default memo(HeaderDropdownMenu);
